import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, FolderOpen } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { useAuthStore } from '../../store/authStore';
import { Project, ProjectStatus, ProfileType } from '../../types';

interface ProjectSelectorProps {
  onProjectSelect: (project: Project) => void;
  selectedProject?: Project | null;
  className?: string;
}

const STATUS_LABELS: Record<ProjectStatus, string> = {
  [ProjectStatus.A_PREPARER]: 'À préparer',
  [ProjectStatus.SOUMIS]: 'Soumis',
  [ProjectStatus.ACCORD_PROVISOIRE]: 'Accord provisoire',
  [ProjectStatus.VALIDE]: 'Validé',
  [ProjectStatus.DEBLOCAGE_FONDS]: 'Déblocage fonds',
  [ProjectStatus.EN_COURS]: 'En cours',
  [ProjectStatus.VERIFICATION_FINALE]: 'Vérification finale',
  [ProjectStatus.TERMINE]: 'Terminé',
  [ProjectStatus.REJETE]: 'Rejeté',
  [ProjectStatus.SUSPENDU]: 'Suspendu'
};

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  onProjectSelect,
  selectedProject,
  className = ''
}) => {
  const { user } = useAuthStore();
  const { projects, fetchProjects, isLoading } = useProjectStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Filter projects based on user role and search term
  const filteredProjects = projects.filter(project => {
    // Role-based filtering
    if (user?.profile_type === ProfileType.PORTEUR) {
      if (project.owner_id !== user.id) return false;
    } else if (user?.profile_type === ProfileType.ACCOMPAGNATEUR) {
      if (project.assigned_advisor_id !== user.id && project.owner_id !== user.id) return false;
    }
    // COORDINATEUR, FINANCEUR, ADMIN can see all projects

    // Search filtering
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        project.name.toLowerCase().includes(searchLower) ||
        project.owner?.full_name?.toLowerCase().includes(searchLower) ||
        project.owner?.email?.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  const handleProjectSelect = (project: Project) => {
    onProjectSelect(project);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className={`relative ${className}`}>
      {/* Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
      >
        <div className="flex items-center space-x-3">
          <FolderOpen className="w-5 h-5 text-gray-400" />
          <div className="text-left">
            {selectedProject ? (
              <>
                <p className="font-medium text-gray-900">{selectedProject.name}</p>
                <p className="text-sm text-gray-600">
                  {STATUS_LABELS[selectedProject.current_status || selectedProject.status]}
                </p>
              </>
            ) : (
              <p className="text-gray-600">Sélectionner un projet...</p>
            )}
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Content */}
          <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-96 overflow-hidden">
            {/* Search */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Rechercher un projet..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>

            {/* Projects List */}
            <div className="max-h-64 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FolderOpen className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>
                    {searchTerm ? 'Aucun projet trouvé' : 'Aucun projet disponible'}
                  </p>
                </div>
              ) : (
                filteredProjects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => handleProjectSelect(project)}
                    className="w-full text-left p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{project.name}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-sm text-gray-600">
                            {project.owner?.full_name || project.owner?.email}
                          </span>
                          <span className="text-gray-300">•</span>
                          <span className="text-sm text-blue-600">
                            {STATUS_LABELS[project.current_status || project.status]}
                          </span>
                        </div>
                      </div>
                      {selectedProject?.id === project.id && (
                        <div className="w-2 h-2 bg-blue-600 rounded-full ml-2" />
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};